import React, { useState, useEffect } from 'react';
import { AlertTriangle, Info, Palette, Home as HomeIcon, Plane, ClipboardList, LayoutGrid, BarChart3, Save } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../services/api';
import { sportsApi } from '../services/sportsApi';
import { User } from '../types/auth';
import { Sport, Club } from '../types/sports';
import { getToken } from '../utils/auth';

const Profile: React.FC = () => {
  const { user: contextUser, refreshUser } = useAuth();
  const [profile, setProfile] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'account' | 'roles' | 'sports'>('account');

  // Sports data
  const [sports, setSports] = useState<Sport[]>([]);
  const [clubs, setClubs] = useState<Club[]>([]);
  const [selectedSportId, setSelectedSportId] = useState<number | undefined>();
  const [selectedClubId, setSelectedClubId] = useState<number | undefined>();
  const [homeMatchColor, setHomeMatchColor] = useState<string>('#4CAF50'); // Default green
  const [awayMatchColor, setAwayMatchColor] = useState<string>('#FF9800'); // Default orange
  const [preferredViewMode, setPreferredViewMode] = useState<string>('cards'); // Default cards
  const [showDrivingTimes, setShowDrivingTimes] = useState<boolean>(false);
  const [savingPreferences, setSavingPreferences] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [loadingClubs, setLoadingClubs] = useState(false);
  const [clubsError, setClubsError] = useState<string>('');

  // Role management state
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [savingRoles, setSavingRoles] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setIsLoading(true);
        const profileData = await authApi.getProfile();
        setProfile(profileData);
      } catch (err: any) {
        setError(err.message || 'Failed to load profile');
        setProfile(contextUser);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, [contextUser]);

  // Load sports data
  useEffect(() => {
    const loadSportsData = async () => {
      try {
        const sportsData = await sportsApi.getAllSports();
        setSports(sportsData);

        if (profile?.preferredSportId) {
          setSelectedSportId(profile.preferredSportId);
        }
        if (profile?.preferredClubId) {
          setSelectedClubId(profile.preferredClubId);
        }
        if (profile?.homeMatchColor) {
          setHomeMatchColor(profile.homeMatchColor);
        }
        if (profile?.awayMatchColor) {
          setAwayMatchColor(profile.awayMatchColor);
        }
        if (profile?.preferredViewMode) {
          setPreferredViewMode(profile.preferredViewMode);
        }
        if (profile?.showDrivingTimes !== undefined) {
          setShowDrivingTimes(profile.showDrivingTimes);
        }

        // Reset unsaved changes flag when loading initial data
        setHasUnsavedChanges(false);
      } catch (err) {
        console.error('Error loading sports data:', err);
      }
    };

    if (profile) {
      loadSportsData();
    }
  }, [profile]);

  // Load clubs when sport is selected
  useEffect(() => {
    const loadClubs = async () => {
      if (selectedSportId) {
        try {
          setLoadingClubs(true);
          setClubsError('');
          console.log('Loading clubs for sport ID:', selectedSportId);

          const clubsData = await sportsApi.getAllClubs(selectedSportId);
          console.log('Clubs loaded:', clubsData);

          // Ensure clubsData is an array
          const safeClubsData = Array.isArray(clubsData) ? clubsData : [];
          console.log('Number of clubs:', safeClubsData.length);

          // Debug: Log first club to see structure
          if (safeClubsData.length > 0) {
            console.log('First club structure:', safeClubsData[0]);
            console.log('First club league:', safeClubsData[0].league);
          }

          setClubs(safeClubsData);
        } catch (err: any) {
          console.error('Error loading clubs:', err);
          setClubsError(err.message || 'Fehler beim Laden der Vereine');
          setClubs([]);
        } finally {
          setLoadingClubs(false);
        }
      } else {
        setClubs([]);
        setSelectedClubId(undefined);
        setClubsError('');
      }
    };

    loadClubs();
  }, [selectedSportId]);

  const handleSportChange = (sportId: number) => {
    setSelectedSportId(sportId);
    setSelectedClubId(undefined); // Reset club when sport changes
    setHasUnsavedChanges(true);
  };

  const handleClubChange = (clubId: number) => {
    setSelectedClubId(clubId);
    setHasUnsavedChanges(true);
  };

  const handleSavePreferences = async () => {
    try {
      setSavingPreferences(true);
      setError('');

      const token = getToken();
      if (!token) {
        throw new Error('Bitte melden Sie sich erneut an.');
      }

      console.log('Saving preferences...');
      console.log('Selected Sport ID:', selectedSportId);
      console.log('Selected Club ID:', selectedClubId);
      console.log('Home Match Color:', homeMatchColor);
      console.log('Away Match Color:', awayMatchColor);

      // Save sport preference using sportsApi
      if (selectedSportId !== profile?.preferredSportId) {
        console.log('Saving sport preference...');
        await sportsApi.updateSportPreference({ sportId: selectedSportId });
        console.log('Sport preference saved');
      }

      // Save club preference using sportsApi
      if (selectedClubId !== profile?.preferredClubId) {
        console.log('Saving club preference...');
        await sportsApi.updateClubPreference({ clubId: selectedClubId });
        console.log('Club preference saved');
      }

      // Save match color preferences
      if (homeMatchColor !== profile?.homeMatchColor || awayMatchColor !== profile?.awayMatchColor) {
        console.log('Saving match color preferences...');
        await sportsApi.updateMatchColorPreferences(homeMatchColor, awayMatchColor);
        console.log('Match color preferences saved');
      }

      // Save view mode preference
      if (preferredViewMode !== profile?.preferredViewMode) {
        console.log('Saving view mode preference...');
        await sportsApi.updateViewModePreference(preferredViewMode);
        console.log('View mode preference saved');
      }

      // Save driving times preference
      if (showDrivingTimes !== profile?.showDrivingTimes) {
        console.log('Saving driving times preference...');
        await sportsApi.updateDrivingTimesPreference(showDrivingTimes);
        console.log('Driving times preference saved');
      }

      // Reload profile to get updated data
      console.log('Reloading profile...');
      const updatedProfile = await authApi.getProfile();
      setProfile(updatedProfile);
      console.log('Profile reloaded:', updatedProfile);

      // Refresh the AuthContext user so other components get the updated data
      await refreshUser();
      console.log('AuthContext user refreshed');

      setHasUnsavedChanges(false);
      setError('');

      console.log('Preferences saved successfully!');

    } catch (err: any) {
      console.error('Error saving preferences:', err);
      setError(err.message || 'Fehler beim Speichern der Präferenzen');
    } finally {
      setSavingPreferences(false);
    }
  };

  // Initialize selected roles when profile loads
  useEffect(() => {
    if (profile?.roles) {
      setSelectedRoles([...profile.roles]);
    }
  }, [profile]);

  const handleRoleToggle = (role: string, checked: boolean) => {
    setSelectedRoles(prev => {
      if (checked) {
        return [...prev, role];
      } else {
        return prev.filter(r => r !== role);
      }
    });
  };

  const handleSaveRoles = async () => {
    if (!profile) return;

    try {
      setSavingRoles(true);
      setError('');

      // Get user ID from profile (we need to extract it from the token or add it to profile)
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Decode token to get user ID
      const tokenPayload = JSON.parse(atob(token.split('.')[1]));
      const userId = tokenPayload.sub || tokenPayload.nameid;

      if (!userId) {
        throw new Error('User ID not found in token');
      }

      const updatedProfile = await authApi.updateUserRoles(userId, selectedRoles);
      setProfile(updatedProfile);

      // Show success message briefly
      setError('');
      setTimeout(() => {
        // Could add a success message state if needed
      }, 2000);

    } catch (err: any) {
      console.error('Error updating roles:', err);
      setError(err.message || 'Fehler beim Speichern der Rollen');
      // Reset selected roles to current profile roles
      if (profile?.roles) {
        setSelectedRoles([...profile.roles]);
      }
    } finally {
      setSavingRoles(false);
    }
  };

  if (isLoading) {
    return (
      <div className="page-container" style={containerStyle}>
        <div style={loadingStyle}>Loading profile...</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="page-container" style={containerStyle}>
        <div style={errorStyle}>Failed to load profile</div>
      </div>
    );
  }

  return (
    <div className="page-container" style={containerStyle}>
      <div style={profileCardStyle}>
        <h1 style={titleStyle}>User Profile</h1>

        {error && (
          <div style={errorStyle}>
            {error}
          </div>
        )}

        {/* Tab Navigation */}
        <div style={tabContainerStyle}>
          <button
            style={{
              ...tabButtonStyle,
              ...(activeTab === 'account' ? activeTabButtonStyle : {})
            }}
            onClick={() => setActiveTab('account')}
          >
            Account Information
          </button>
          <button
            style={{
              ...tabButtonStyle,
              ...(activeTab === 'roles' ? activeTabButtonStyle : {})
            }}
            onClick={() => setActiveTab('roles')}
          >
            Role Management
          </button>
          <button
            style={{
              ...tabButtonStyle,
              ...(activeTab === 'sports' ? activeTabButtonStyle : {})
            }}
            onClick={() => setActiveTab('sports')}
          >
            Sports Preferences
          </button>
        </div>

        {/* Tab Content */}
        <div style={tabContentStyle}>
          {/* Account Tab */}
          {activeTab === 'account' && (
            <div style={profileDetailsStyle}>
              <div style={detailSectionStyle}>
                <h3 style={sectionTitleStyle}>Personal Information</h3>
                <div style={detailRowStyle}>
                  <label style={detailLabelStyle}>Email:</label>
                  <span style={detailValueStyle}>{profile.email}</span>
                </div>
                <div style={detailRowStyle}>
                  <label style={detailLabelStyle}>Display Name:</label>
                  <span style={detailValueStyle}>{profile.displayName || 'Not set'}</span>
                </div>
                <div style={detailRowStyle}>
                  <label style={detailLabelStyle}>Account Type:</label>
                  <span style={detailValueStyle}>
                    {profile.isGoogleAccount ? 'Google Account' : 'Email Account'}
                  </span>
                </div>
                <div style={detailRowStyle}>
                  <label style={detailLabelStyle}>Member Since:</label>
                  <span style={detailValueStyle}>
                    {new Date(profile.createdUtc).toLocaleDateString()}
                  </span>
                </div>
              </div>

              <div style={detailSectionStyle}>
                <h3 style={sectionTitleStyle}>Access Permissions</h3>
                <div style={rolesContainerStyle}>
                  {profile?.roles && profile?.roles.length > 0 ? (
                    profile?.roles.map((role, index) => (
                      <div key={index} style={roleTagStyle}>
                        {role}
                      </div>
                    ))
                  ) : (
                    <div style={noRolesStyle}>No roles assigned</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Roles Tab */}
          {activeTab === 'roles' && (
            <div style={profileDetailsStyle}>
              <div style={detailSectionStyle}>
                <h3 style={sectionTitleStyle}>Role Management</h3>
                <p style={descriptionStyle}>
                  {profile?.roles?.includes('Admin')
                    ? 'Manage user roles and permissions as an administrator.'
                    : 'View your current roles. Only administrators can modify roles.'
                  }
                </p>

                <div style={{ marginTop: '1.5rem' }}>
                  <label style={detailLabelStyle}>Current Roles:</label>
                  <div style={rolesDisplayStyle}>
                    {profile?.roles && profile?.roles.length > 0 ? (
                      profile?.roles.map((role, index) => (
                        <span key={index} style={roleTagStyle}>
                          {role}
                        </span>
                      ))
                    ) : (
                      <span style={noRolesStyle}>No roles assigned</span>
                    )}
                  </div>
                </div>

                {profile?.roles?.includes('Admin') && (
                  <>
                    <div style={{ marginTop: '1.5rem' }}>
                      <label style={detailLabelStyle}>Add/Remove Roles:</label>
                      <div style={{ marginTop: '0.5rem' }}>
                        <label style={checkboxLabelStyle}>
                          <input
                            type="checkbox"
                            checked={selectedRoles.includes('Admin')}
                            onChange={(e) => handleRoleToggle('Admin', e.target.checked)}
                            style={checkboxStyle}
                            disabled={savingRoles}
                          />
                          Admin
                        </label>
                        <label style={checkboxLabelStyle}>
                          <input
                            type="checkbox"
                            checked={selectedRoles.includes('User')}
                            onChange={(e) => handleRoleToggle('User', e.target.checked)}
                            style={checkboxStyle}
                            disabled={savingRoles}
                          />
                          User
                        </label>
                      </div>
                    </div>

                    <button
                      style={{
                        ...saveButtonStyle,
                        opacity: savingRoles ? 0.6 : 1,
                        cursor: savingRoles ? 'not-allowed' : 'pointer'
                      }}
                      onClick={handleSaveRoles}
                      disabled={savingRoles}
                    >
                      {savingRoles ? 'Speichere...' : 'Save Role Changes'}
                    </button>
                  </>
                )}

                {!profile?.roles?.includes('Admin') && (
                  <div style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px', border: '1px solid #dee2e6' }}>
                    <p style={{ margin: 0, color: '#6c757d', fontStyle: 'italic' }}>
                      Sie haben keine Berechtigung, Rollen zu ändern. Nur Administratoren können Benutzerrollen verwalten.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Sports Tab */}
          {activeTab === 'sports' && (
            <div style={profileDetailsStyle}>
              {savingPreferences && (
                <div style={savingIndicatorStyle}>
                  <span>Speichere Präferenzen...</span>
                </div>
              )}

              <div style={detailSectionStyle}>
                <h3 style={sectionTitleStyle}>Sports Preferences</h3>
                <p style={descriptionStyle}>
                  Wählen Sie Ihre bevorzugte Sportart und Ihren Lieblingsverein aus.
                </p>

                <div style={{ marginTop: '1.5rem' }}>
                  <label style={detailLabelStyle}>Preferred Sport:</label>
                  <select
                    value={selectedSportId || ''}
                    onChange={(e) => {
                      const sportId = parseInt(e.target.value);
                      if (sportId) {
                        handleSportChange(sportId);
                      } else {
                        setSelectedSportId(undefined);
                        setSelectedClubId(undefined);
                        setHasUnsavedChanges(true);
                      }
                    }}
                    style={selectStyle}
                    disabled={savingPreferences}
                  >
                    <option value="">Bitte wählen...</option>
                    {sports.map((sport) => (
                      <option key={sport.id} value={sport.id}>
                        {sport.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <label style={detailLabelStyle}>Preferred Club:</label>
                  <select
                    value={selectedClubId || ''}
                    onChange={(e) => {
                      const clubId = parseInt(e.target.value);
                      if (clubId) {
                        handleClubChange(clubId);
                      } else {
                        setSelectedClubId(undefined);
                        setHasUnsavedChanges(true);
                      }
                    }}
                    style={selectStyle}
                    disabled={savingPreferences || !selectedSportId || loadingClubs}
                  >
                    <option value="">
                      {loadingClubs ? 'Lade Vereine...' : 'Bitte wählen...'}
                    </option>
                    {clubs.map((club) => {
                      // Build display text with available information
                      let displayText = club.name;

                      // Add league name if available
                      if (club.league?.name) {
                        displayText += ` (${club.league.name})`;
                      }
                      // Fallback to country if league is not available
                      else if (club.country?.name) {
                        displayText += ` (${club.country.name})`;
                      }
                      // Fallback to city if available
                      else if (club.city) {
                        displayText += ` (${club.city})`;
                      }

                      return (
                        <option key={club.id} value={club.id}>
                          {displayText}
                        </option>
                      );
                    })}
                  </select>
                  {!selectedSportId && (
                    <p style={hintTextStyle}>
                      Wählen Sie zuerst eine Sportart aus.
                    </p>
                  )}
                  {clubsError && (
                    <p style={{ ...hintTextStyle, color: '#dc3545' }}>
                      <AlertTriangle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> {clubsError}
                    </p>
                  )}
                  {selectedSportId && !loadingClubs && clubs.length === 0 && !clubsError && (
                    <p style={hintTextStyle}>
                      <Info size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Keine Vereine für diese Sportart gefunden.
                    </p>
                  )}
                  {loadingClubs && (
                    <p style={hintTextStyle}>
                      ⏳ Lade Vereine...
                    </p>
                  )}
                </div>

                {/* Match Color Preferences */}
                <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid #e0e0e0' }}>
                  <h4 style={{ ...sectionTitleStyle, fontSize: '1.2rem', marginBottom: '0.5rem' }}>
                    <Palette size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} /> Spielfarben
                  </h4>
                  <p style={descriptionStyle}>
                    Wählen Sie Farben für Heim- und Auswärtsspiele in der Kalenderansicht.
                  </p>

                  <div className="detail-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '1rem' }}>
                    {/* Home Match Color */}
                    <div>
                      <label style={detailLabelStyle}><HomeIcon size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Heimspiele:</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.5rem' }}>
                        <input
                          type="color"
                          value={homeMatchColor}
                          onChange={(e) => {
                            setHomeMatchColor(e.target.value);
                            setHasUnsavedChanges(true);
                          }}
                          style={{
                            width: '60px',
                            height: '40px',
                            border: '2px solid #ddd',
                            borderRadius: '6px',
                            cursor: 'pointer',
                          }}
                          disabled={savingPreferences}
                        />
                        <span style={{
                          fontSize: '0.9rem',
                          color: '#666',
                          fontFamily: 'monospace',
                          backgroundColor: '#f5f5f5',
                          padding: '0.5rem 1rem',
                          borderRadius: '4px',
                          flex: 1,
                        }}>
                          {homeMatchColor}
                        </span>
                      </div>
                    </div>

                    {/* Away Match Color */}
                    <div>
                      <label style={detailLabelStyle}><Plane size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Auswärtsspiele:</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.5rem' }}>
                        <input
                          type="color"
                          value={awayMatchColor}
                          onChange={(e) => {
                            setAwayMatchColor(e.target.value);
                            setHasUnsavedChanges(true);
                          }}
                          style={{
                            width: '60px',
                            height: '40px',
                            border: '2px solid #ddd',
                            borderRadius: '6px',
                            cursor: 'pointer',
                          }}
                          disabled={savingPreferences}
                        />
                        <span style={{
                          fontSize: '0.9rem',
                          color: '#666',
                          fontFamily: 'monospace',
                          backgroundColor: '#f5f5f5',
                          padding: '0.5rem 1rem',
                          borderRadius: '4px',
                          flex: 1,
                        }}>
                          {awayMatchColor}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Color Preview */}
                  <div style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: '#f9f9f9', borderRadius: '6px' }}>
                    <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.75rem', fontWeight: '600' }}>
                      Vorschau:
                    </p>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                      <div style={{
                        flex: 1,
                        padding: '0.75rem',
                        backgroundColor: homeMatchColor,
                        color: 'white',
                        borderRadius: '4px',
                        textAlign: 'center',
                        fontWeight: '600',
                        textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                      }}>
                        <HomeIcon size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Heimspiel
                      </div>
                      <div style={{
                        flex: 1,
                        padding: '0.75rem',
                        backgroundColor: awayMatchColor,
                        color: 'white',
                        borderRadius: '4px',
                        textAlign: 'center',
                        fontWeight: '600',
                        textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                      }}>
                        <Plane size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Auswärtsspiel
                      </div>
                    </div>
                  </div>
                </div>

                {/* View Mode Preference */}
                <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid #e0e0e0' }}>
                  <h4 style={{ ...sectionTitleStyle, fontSize: '1.2rem', marginBottom: '0.5rem' }}>
                    <ClipboardList size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} /> Standard-Ansicht
                  </h4>
                  <p style={descriptionStyle}>
                    Wählen Sie die Standardansicht für alle Formulare mit Kachel-/Listenansicht.
                  </p>

                  <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                    <button
                      onClick={() => {
                        setPreferredViewMode('cards');
                        setHasUnsavedChanges(true);
                      }}
                      disabled={savingPreferences}
                      style={{
                        flex: 1,
                        padding: '1rem',
                        border: preferredViewMode === 'cards' ? '3px solid #4CAF50' : '2px solid #ddd',
                        borderRadius: '8px',
                        backgroundColor: preferredViewMode === 'cards' ? '#e8f5e9' : '#fff',
                        cursor: savingPreferences ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      <span style={{ fontSize: '2rem' }}><LayoutGrid size={28} /></span>
                      <span style={{ fontWeight: '600', color: preferredViewMode === 'cards' ? '#2e7d32' : '#333' }}>
                        Kacheln
                      </span>
                      <span style={{ fontSize: '0.8rem', color: '#666' }}>
                        Kompakte Kartenansicht
                      </span>
                    </button>

                    <button
                      onClick={() => {
                        setPreferredViewMode('plantafel');
                        setHasUnsavedChanges(true);
                      }}
                      disabled={savingPreferences}
                      style={{
                        flex: 1,
                        padding: '1rem',
                        border: preferredViewMode === 'plantafel' ? '3px solid #4CAF50' : '2px solid #ddd',
                        borderRadius: '8px',
                        backgroundColor: preferredViewMode === 'plantafel' ? '#e8f5e9' : '#fff',
                        cursor: savingPreferences ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      <span style={{ fontSize: '2rem' }}><BarChart3 size={28} /></span>
                      <span style={{ fontWeight: '600', color: preferredViewMode === 'plantafel' ? '#2e7d32' : '#333' }}>
                        Plantafel
                      </span>
                      <span style={{ fontSize: '0.8rem', color: '#666' }}>
                        Listenansicht
                      </span>
                    </button>
                  </div>
                </div>

                {/* Driving Times Toggle */}
                <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid #e0e0e0' }}>
                  <h4 style={{ ...sectionTitleStyle, fontSize: '1.2rem', marginBottom: '0.5rem' }}>
                    Lenk- & Ruhezeiten
                  </h4>
                  <p style={descriptionStyle}>
                    Zeige kumulierte Lenk- und Schichtzeiten in der Planungstimeline an.
                  </p>

                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    marginTop: '1rem',
                    cursor: savingPreferences ? 'not-allowed' : 'pointer',
                    fontSize: '1rem'
                  }}>
                    <input
                      type="checkbox"
                      checked={showDrivingTimes}
                      onChange={(e) => {
                        setShowDrivingTimes(e.target.checked);
                        setHasUnsavedChanges(true);
                      }}
                      disabled={savingPreferences}
                      style={{ width: '20px', height: '20px', accentColor: '#4CAF50' }}
                    />
                    <span style={{ fontWeight: '600', color: showDrivingTimes ? '#2e7d32' : '#333' }}>
                      Lenkzeiten anzeigen
                    </span>
                  </label>
                </div>

                {/* Save Button and Status */}
                <div style={{ marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid #e0e0e0' }}>
                  {hasUnsavedChanges && (
                    <p style={{
                      color: '#ff9800',
                      fontSize: '0.9rem',
                      marginBottom: '1rem',
                      fontStyle: 'italic'
                    }}>
                      <AlertTriangle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Sie haben ungespeicherte Änderungen
                    </p>
                  )}

                  <button
                    onClick={handleSavePreferences}
                    disabled={savingPreferences || !hasUnsavedChanges}
                    style={{
                      backgroundColor: hasUnsavedChanges ? '#4CAF50' : '#cccccc',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '6px',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: hasUnsavedChanges && !savingPreferences ? 'pointer' : 'not-allowed',
                      transition: 'all 0.2s ease',
                      opacity: savingPreferences ? 0.7 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    {savingPreferences ? (
                      <>
                        <span style={{
                          display: 'inline-block',
                          width: '16px',
                          height: '16px',
                          border: '2px solid #ffffff40',
                          borderTop: '2px solid #ffffff',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }}></span>
                        Speichere...
                      </>
                    ) : (
                      <>
                        <Save size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} /> Präferenzen speichern
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Add CSS animation for spinner
const spinnerKeyframes = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Inject the keyframes into the document head
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = spinnerKeyframes;
  document.head.appendChild(style);
}

export default Profile;

// Styles
const containerStyle: React.CSSProperties = {
  minHeight: '100vh',
  backgroundColor: '#f5f5f5',
  padding: '2rem',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
};

const profileCardStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '8px',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
  padding: '2rem',
  width: '100%',
  maxWidth: '800px',
};

const titleStyle: React.CSSProperties = {
  color: '#2c3e50',
  marginBottom: '2rem',
  textAlign: 'center',
  fontSize: '2rem',
  fontWeight: 'bold',
};

const loadingStyle: React.CSSProperties = {
  textAlign: 'center',
  padding: '2rem',
  fontSize: '1.2rem',
  color: '#666',
};

const errorStyle: React.CSSProperties = {
  backgroundColor: '#f8d7da',
  color: '#721c24',
  padding: '1rem',
  borderRadius: '4px',
  marginBottom: '1rem',
  border: '1px solid #f5c6cb',
};

const tabContainerStyle: React.CSSProperties = {
  display: 'flex',
  borderBottom: '2px solid #e9ecef',
  marginBottom: '2rem',
};

const tabButtonStyle: React.CSSProperties = {
  backgroundColor: 'transparent',
  border: 'none',
  padding: '1rem 1.5rem',
  cursor: 'pointer',
  fontSize: '1rem',
  color: '#6c757d',
  borderBottom: '2px solid transparent',
  transition: 'all 0.2s ease',
};

const activeTabButtonStyle: React.CSSProperties = {
  color: '#007bff',
  borderBottomColor: '#007bff',
  fontWeight: '600',
};

const tabContentStyle: React.CSSProperties = {
  minHeight: '400px',
};

const profileDetailsStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '2rem',
};

const detailSectionStyle: React.CSSProperties = {
  backgroundColor: '#f8f9fa',
  padding: '1.5rem',
  borderRadius: '6px',
  border: '1px solid #dee2e6',
};

const sectionTitleStyle: React.CSSProperties = {
  color: '#495057',
  marginBottom: '1rem',
  fontSize: '1.25rem',
  fontWeight: '600',
};

const detailRowStyle: React.CSSProperties = {
  display: 'flex',
  marginBottom: '0.75rem',
  alignItems: 'center',
};

const detailLabelStyle: React.CSSProperties = {
  fontWeight: '600',
  color: '#495057',
  minWidth: '150px',
  marginRight: '1rem',
};

const detailValueStyle: React.CSSProperties = {
  color: '#6c757d',
  flex: 1,
};

const rolesContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '0.5rem',
  marginTop: '1rem',
};

const roleTagStyle: React.CSSProperties = {
  backgroundColor: '#3498db',
  color: 'white',
  padding: '0.5rem 1rem',
  borderRadius: '20px',
  fontSize: '0.875rem',
  fontWeight: '500',
};

const noRolesStyle: React.CSSProperties = {
  color: '#95a5a6',
  fontStyle: 'italic',
};

const descriptionStyle: React.CSSProperties = {
  color: '#666',
  lineHeight: '1.6',
  marginBottom: '1rem',
};

const rolesDisplayStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '0.5rem',
  marginTop: '0.5rem',
};

const checkboxLabelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  marginBottom: '0.5rem',
  cursor: 'pointer',
};

const checkboxStyle: React.CSSProperties = {
  marginRight: '0.5rem',
};

const saveButtonStyle: React.CSSProperties = {
  backgroundColor: '#27ae60',
  color: 'white',
  border: 'none',
  padding: '0.75rem 1.5rem',
  borderRadius: '4px',
  fontSize: '1rem',
  cursor: 'pointer',
  marginTop: '1rem',
  transition: 'background-color 0.2s ease',
};

const savingIndicatorStyle: React.CSSProperties = {
  backgroundColor: '#d1ecf1',
  color: '#0c5460',
  padding: '0.75rem',
  borderRadius: '4px',
  marginBottom: '1rem',
  border: '1px solid #bee5eb',
  textAlign: 'center',
};

const selectStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.75rem',
  border: '1px solid #ced4da',
  borderRadius: '4px',
  fontSize: '1rem',
  backgroundColor: 'white',
  color: '#495057',
};

const hintTextStyle: React.CSSProperties = {
  color: '#6c757d',
  fontSize: '0.875rem',
  fontStyle: 'italic',
  marginTop: '0.5rem',
};